import type { CharacterProgression } from "@/game/model/character-progression";
import type { StatId } from "@/game/model/character-stats";
import type { HomeEconomy } from "@/home/economy";
import type { HomeRoomData } from "@/home/storage";

export const HomeEvents = {
  DataChanged: "home-data-changed",
  EditModeChanged: "home-edit-mode-changed",
  MovePlacement: "home-move-placement",
  RemovePlacement: "home-remove-placement",
  SelectPlacement: "home-select-placement",
  AdjustPartDepthBias: "home-adjust-part-depth-bias",
  AdjustPlacementTransform: "home-adjust-placement-transform",
  UseSkill: "home-use-skill",
  SkillRuntimeChanged: "home-skill-runtime-changed",
  CombatActiveChanged: "home-combat-active-changed",
  SitRestChanged: "home-sit-rest-changed",
  ProgressionChanged: "home-progression-changed",
  EquipmentChanged: "home-equipment-changed",
  OpenSkillsModal: "home-open-skills-modal",
  OpenCharacterShadowTab: "home-open-character-shadow-tab",
  ShadowChanged: "home-shadow-changed",
  PetChanged: "home-pet-changed",
  MonsterDefeated: "home-monster-defeated",
  EconomyChanged: "home-economy-changed",
  RoomSceneReady: "home-room-scene-ready",
  StructureAssaultEnded: "home-structure-assault-ended",
} as const;

export type HomeStructureAssaultEndedPayload = {
  damageThisRun: number;
  destroyed: boolean;
};

export type HomeDataChangedPayload = { data: HomeRoomData };
export type HomeEditModePayload = { enabled: boolean };
export type HomeMovePlacementPayload = {
  placementId: string;
  gridX: number;
  gridY: number;
};
export type HomeRemovePlacementPayload = { placementId: string };
export type HomeSelectPlacementPayload = {
  placementId: string | null;
  partId: string | null;
};

export type HomeAdjustPartDepthBiasPayload = {
  placementId: string;
  delta: number;
};

export type HomeAdjustPlacementTransformPayload = {
  placementId: string;
  action: "flipX" | "flipY" | "rotate";
};

export type HomeUseSkillPayload = { skillId: string; slotIndex: number };
export type HomeCombatActivePayload = { active: boolean };
export type HomeSitRestPayload = { enabled: boolean };

export type HomeSkillRuntimeSkill = {
  id: string;
  name: string;
  kind: "attack" | "buff";
  durationMs: number;
  spritesheetDataUrl: string;
  grid: import("@/character/grid").CharacterGridSpec;
  frameRate: number;
  cooldownMs: number;
  damageMultiplier?: number;
  attackPattern?: import("@/character/skills/types").SkillAttackPattern;
  /** AOE attack — scale for FX and hit area (1 = base). */
  aoeRangeMultiplier?: number;
  buffStatBonus?: Partial<Record<StatId, number>>;
};

export type HomeProgressionChangedPayload = {
  progression: CharacterProgression;
  /** Set when EXP grant caused one or more level increases. */
  levelsGained?: number;
};
export type HomeEquipmentChangedPayload = {
  bonus: Partial<import("@/game/model/character-stats").StatAllocation> | undefined;
};
export type HomeMonsterDefeatedPayload = { placementId: string; xpReward: number };
export type HomeEconomyChangedPayload = { economy: HomeEconomy };

export type HomeShadowChangedPayload = {
  state: import("@/shadow/types").ShadowPlayerState;
  equipped: import("@/shadow/types").ShadowDefinition | null;
};

export type HomeSkillRuntimePayload = {
  unlockedSlots: number;
  cooldownMs: number;
  slots: (string | null)[];
  skills: HomeSkillRuntimeSkill[];
};

export const HOME_PART_DRAG_MIME = "application/x-home-part";
